import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../api";
import { useAuth } from "../AuthContext";
import { useI18n } from "../I18nContext";

function pickMsgContent(m, lang) {
  return m.content || (lang === "fr" ? m.content_fr : m.content_en) || m.content_en || m.content_fr || "";
}

export default function Onboarding() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const nav = useNavigate();
  const [session, setSession] = useState(null);
  const [progress, setProgress] = useState({ brain_score: 0, phase: "foundation" });
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const scrollRef = useRef(null);

  const eligible = user && user.role === "entrepreneur";

  useEffect(() => {
    if (!eligible) return;
    api.get("/onboarding/state")
      .then((r) => {
        if (r.data.session) {
          setSession(r.data.session);
          setProgress(r.data.progress || {});
        }
      })
      .catch((e) => setErr(formatApiErrorDetail(e) || ""));
  }, [eligible]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [session]);

  const start = async () => {
    setBusy(true);
    setErr("");
    try {
      const r = await api.post("/onboarding/start");
      setSession(r.data.session);
      setProgress(r.data.progress || {});
    } catch (e) {
      setErr(formatApiErrorDetail(e) || t("onboarding.error"));
    } finally {
      setBusy(false);
    }
  };

  const send = async () => {
    const msg = input.trim();
    if (!msg || busy) return;
    setBusy(true);
    setErr("");
    // Optimistic user msg
    const optimistic = {
      ...session,
      messages: [...(session.messages || []), { role: "user", content: msg, at: new Date().toISOString() }],
    };
    setSession(optimistic);
    setInput("");
    try {
      const r = await api.post("/onboarding/reply", { message: msg, lang });
      setSession(r.data.session);
      setProgress(r.data.progress || {});
    } catch (e) {
      setErr(formatApiErrorDetail(e) || t("onboarding.error"));
    } finally {
      setBusy(false);
    }
  };

  const dismiss = async () => {
    if (!window.confirm(t("onboarding.confirmDismiss"))) return;
    await api.post("/onboarding/dismiss");
    nav("/app");
  };

  const complete = async () => {
    if (!window.confirm(t("onboarding.confirmComplete"))) return;
    await api.post("/onboarding/complete");
    nav("/app");
  };

  const messages = useMemo(() => (session?.messages || []).map((m, i) => ({ ...m, key: i })), [session]);

  if (!eligible) {
    return (
      <div className="tb-page">
        <h1>{t("onboarding.title")}</h1>
        <div className="tb-card"><p className="tb-muted">{t("onboarding.foundersOnly")}</p></div>
      </div>
    );
  }

  return (
    <div className="tb-page tb-onboarding-page" data-testid="onboarding-page">
      <header className="tb-onb-header">
        <div>
          <h1>{t("onboarding.title")}</h1>
          <p className="tb-muted">{t("onboarding.subtitle")}</p>
        </div>
        <div className="tb-onb-progress" data-testid="onb-progress">
          <div className="tb-onb-score" title={t("onboarding.brainScoreHint")}>
            <span className="tb-onb-score-num">{progress.brain_score || 0}%</span>
            <span className="tb-onb-score-label">{t("onboarding.brainScore")}</span>
          </div>
          <div className="tb-onb-phase" data-testid="onb-phase">
            {t(`onboarding.phases.${progress.phase || "foundation"}`)}
          </div>
        </div>
      </header>

      {err && <div className="tb-error">{err}</div>}

      {!session && (
        <div className="tb-onb-welcome">
          <h2>{t("onboarding.welcomeTitle")}</h2>
          <p>{t("onboarding.welcomeBody")}</p>
          <button
            type="button"
            className="tb-primary-btn"
            onClick={start}
            disabled={busy}
            data-testid="onb-start"
          >
            {busy ? t("onboarding.starting") : t("onboarding.start")}
          </button>
        </div>
      )}

      {session && (
        <>
          <div className="tb-onb-chat" ref={scrollRef} data-testid="onb-chat">
            {messages.map((m) => (
              <div
                key={m.key}
                className={`tb-onb-msg tb-onb-msg-${m.role}`}
                data-testid={`onb-msg-${m.role}-${m.key}`}
              >
                <div className="tb-onb-msg-role">
                  {m.role === "user" ? t("onboarding.you") : t("onboarding.brain")}
                </div>
                <div className="tb-onb-msg-content">
                  {pickMsgContent(m, lang).split("\n").map((line, i) => (
                    <p key={i}>{line}</p>
                  ))}
                </div>
              </div>
            ))}
            {busy && (
              <div className="tb-onb-msg tb-onb-msg-assistant tb-onb-typing" data-testid="onb-typing">
                <div className="tb-onb-msg-role">{t("onboarding.brain")}</div>
                <div className="tb-onb-msg-content">
                  <span className="tb-dot" /><span className="tb-dot" /><span className="tb-dot" />
                </div>
              </div>
            )}
          </div>

          <div className="tb-onb-composer">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              placeholder={t("onboarding.placeholder")}
              rows={3}
              disabled={busy}
              data-testid="onb-input"
            />
            <div className="tb-onb-composer-foot">
              <div className="tb-onb-composer-actions">
                <button
                  type="button"
                  className="tb-ghost-btn tb-mini"
                  onClick={dismiss}
                  data-testid="onb-dismiss"
                >
                  {t("onboarding.dismiss")}
                </button>
                <button
                  type="button"
                  className="tb-ghost-btn tb-mini"
                  onClick={complete}
                  data-testid="onb-complete"
                >
                  {t("onboarding.finish")}
                </button>
              </div>
              <button
                type="button"
                className="tb-primary-btn"
                onClick={send}
                disabled={busy || !input.trim()}
                data-testid="onb-send"
              >
                {busy ? t("onboarding.sending") : t("onboarding.send")}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
